<script>
import { createProductLabel, deleteProductLabel, getProductLabels, updateProductLabel } from '@/api/productLabel.js'

export default {
  name: 'ProductLabelListPage',
  data() {
    return {
      keyword: '',
      items: [],
      loading: false,
      dialog: false,
      saving: false,
      editingCode: '',
      editingItem: null,
      form: this.emptyForm(),
      deleteDialog: false,
      deleteTarget: null,
      inactiveDialog: false,
      pendingSaveAfterInactiveConfirm: false,
      errorMessage: '',
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    emptyForm() {
      return {
        labelKey: '',
        labelName: '',
        badgeText: '',
        textColor: '#0072CE',
        backgroundColor: '#FFFFFF',
        sortOrder: 0,
        activeYn: 'Y',
      }
    },
    async fetchList() {
      this.loading = true
      try {
        const res = await getProductLabels({ keyword: this.keyword || undefined })
        if (res.data?.code === 0) this.items = res.data.items || []
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editingCode = ''
      this.editingItem = null
      this.form = this.emptyForm()
      this.errorMessage = ''
      this.dialog = true
    },
    openEdit(item) {
      this.editingCode = item.productLabelCode
      this.editingItem = item
      this.form = {
        labelKey: item.labelKey,
        labelName: item.labelName,
        badgeText: item.badgeText,
        textColor: item.textColor || '#0072CE',
        backgroundColor: item.backgroundColor || '#FFFFFF',
        sortOrder: item.sortOrder ?? 0,
        activeYn: item.activeYn || 'Y',
      }
      this.errorMessage = ''
      this.dialog = true
    },
    requestSave() {
      this.errorMessage = ''
      const shouldWarnInactive =
        this.editingItem
        && this.editingItem.activeYn === 'Y'
        && this.form.activeYn === 'N'
        && Number(this.editingItem.productCount || 0) > 0

      if (shouldWarnInactive && !this.pendingSaveAfterInactiveConfirm) {
        this.inactiveDialog = true
        return
      }
      this.save()
    },
    async confirmInactiveAndSave() {
      this.pendingSaveAfterInactiveConfirm = true
      this.inactiveDialog = false
      try {
        await this.save()
      } finally {
        this.pendingSaveAfterInactiveConfirm = false
      }
    },
    async save() {
      if (!this.form.labelKey?.trim() || !this.form.labelName?.trim()) {
        this.errorMessage = '라벨 키와 라벨명은 필수입니다.'
        return
      }
      this.saving = true
      try {
        const payload = {
          ...this.form,
          labelKey: this.form.labelKey.trim().toUpperCase(),
          labelName: this.form.labelName.trim(),
          badgeText: this.form.badgeText?.trim() || this.form.labelName.trim(),
        }
        const res = this.editingCode
          ? await updateProductLabel(this.editingCode, payload)
          : await createProductLabel(payload)
        if (res.data?.code === 0) {
          this.dialog = false
          await this.fetchList()
          return
        }
        this.errorMessage = res.data?.message || '라벨 저장에 실패했습니다.'
      } finally {
        this.saving = false
      }
    },
    requestRemove(item) {
      this.deleteTarget = item
      this.deleteDialog = true
    },
    async remove() {
      if (!this.deleteTarget || Number(this.deleteTarget.productCount || 0) > 0) {
        return
      }
      const res = await deleteProductLabel(this.deleteTarget.productLabelCode)
      if (res.data?.code === 0) {
        this.deleteDialog = false
        this.deleteTarget = null
        await this.fetchList()
      }
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-h5 font-weight-bold">상품 라벨 관리</div>
        <div class="text-body-2 text-grey mt-1">
          상품 목록과 메인 상품 영역에 노출되는 BEST, NEW, SALE, TIME SALE 등의 라벨을 관리합니다.
        </div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">라벨 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mb-5">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="keyword"
              label="라벨 검색"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="fetchList"
            />
          </v-col>
          <v-col cols="12" md="7" class="d-flex justify-end ga-2">
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="fetchList">검색</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table>
        <thead>
          <tr>
            <th>라벨</th>
            <th>라벨 키</th>
            <th>적용 상품 수</th>
            <th>대표 상품</th>
            <th>정렬</th>
            <th>노출</th>
            <th class="text-center">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.productLabelCode">
            <td>
              <v-chip
                size="small"
                :style="{ color: item.textColor, backgroundColor: item.backgroundColor, borderColor: item.textColor }"
                variant="outlined"
                label
              >
                {{ item.badgeText || item.labelName }}
              </v-chip>
            </td>
            <td>{{ item.labelKey }}</td>
            <td>{{ Number(item.productCount || 0).toLocaleString() }}개</td>
            <td class="text-truncate" style="max-width: 360px">{{ item.sampleProductName || '-' }}</td>
            <td>{{ item.sortOrder }}</td>
            <td>
              <v-chip size="small" :color="item.activeYn === 'Y' ? 'success' : 'grey'">
                {{ item.activeYn === 'Y' ? '노출' : '숨김' }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-btn size="small" variant="text" icon="mdi-pencil" @click="openEdit(item)" />
              <v-btn size="small" variant="text" icon="mdi-delete" color="error" @click="requestRemove(item)" />
            </td>
          </tr>
          <tr v-if="!loading && !items.length">
            <td colspan="7" class="text-center py-8 text-grey">등록된 라벨이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="620">
      <v-card rounded="lg">
        <v-card-title>{{ editingCode ? '라벨 수정' : '라벨 등록' }}</v-card-title>
        <v-card-text>
          <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">{{ errorMessage }}</v-alert>
          <v-row>
            <v-col cols="12" md="6"><v-text-field v-model="form.labelKey" label="라벨 키 *" variant="outlined" /></v-col>
            <v-col cols="12" md="6"><v-text-field v-model="form.labelName" label="라벨명 *" variant="outlined" /></v-col>
            <v-col cols="12"><v-text-field v-model="form.badgeText" label="배지 문구" variant="outlined" /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.textColor" label="글자색" variant="outlined" /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="form.backgroundColor" label="배경색" variant="outlined" /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model.number="form.sortOrder" type="number" label="정렬순서" variant="outlined" /></v-col>
            <v-col cols="12">
              <v-switch v-model="form.activeYn" true-value="Y" false-value="N" color="primary" label="노출 여부" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" :loading="saving" @click="requestSave">저장</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="inactiveDialog" max-width="520">
      <v-card rounded="lg">
        <v-card-title>라벨 숨김 확인</v-card-title>
        <v-card-text>
          <p>
            이 라벨이 적용된 상품 {{ Number(editingItem?.productCount || 0).toLocaleString() }}개가
            상품 목록, 오늘의 주요상품, 상단 라벨 영역에서 함께 보이지 않게 됩니다.
          </p>
          <p v-if="editingItem?.sampleProductName" class="mt-2 text-grey">예: {{ editingItem.sampleProductName }}</p>
          <p class="mt-4">계속 진행하시겠습니까?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="inactiveDialog = false">아니오</v-btn>
          <v-btn color="primary" @click="confirmInactiveAndSave">예</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="520">
      <v-card rounded="lg">
        <v-card-title>라벨 삭제</v-card-title>
        <v-card-text>
          <template v-if="Number(deleteTarget?.productCount || 0) > 0">
            <p>
              해당 라벨이 적용된 상품이 있어 라벨을 삭제할 수 없습니다.
            </p>
            <p class="mt-2">적용 상품 수: {{ Number(deleteTarget?.productCount || 0).toLocaleString() }}개</p>
            <p v-if="deleteTarget?.sampleProductName" class="mt-2 text-grey">예: {{ deleteTarget.sampleProductName }}</p>
          </template>
          <template v-else>
            <p>{{ deleteTarget?.labelName }} 라벨을 삭제하시겠습니까?</p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">닫기</v-btn>
          <v-btn v-if="Number(deleteTarget?.productCount || 0) === 0" color="error" @click="remove">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
